import { useEffect, useRef } from 'react';
import apiService from '../../services/api';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const GSI_SRC = 'https://accounts.google.com/gsi/client';

/**
 * "Continue with Google" button.
 *
 * GSI returns an ID token -> POST /auth/google. Existing user logs straight in.
 * A brand-new user needs a company name, so we bubble that up via `onNeedsCompany`
 * and the parent renders the company-name step in the same auth layout.
 */
const GoogleSignInButton = ({ onLogin, onError, onNeedsCompany }) => {
  const btnRef = useRef(null);

  const finishAuth = async (credential) => {
    onError?.('');
    try {
      const { data } = await apiService.googleAuth(credential, '');

      if (data?.needs_company_name) {
        onNeedsCompany?.(credential, data.google || {});
        return;
      }

      if (data?.success && data.token) {
        if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
        onLogin(data.token, {
          planId: data.user?.planId,
          isFreemium: data.user?.isFreemium,
        });
      } else {
        onError?.(data?.message || 'Google sign-in failed.');
      }
    } catch (err) {
      onError?.(err.response?.data?.message || 'Google sign-in failed. Please try again.');
    }
  };

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return undefined;

    let cancelled = false;
    const renderButton = () => {
      if (cancelled || !window.google?.accounts?.id || !btnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (resp) => finishAuth(resp.credential),
      });
      btnRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(btnRef.current, {
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: 360,
      });
    };

    if (window.google?.accounts?.id) {
      renderButton();
      return () => { cancelled = true; };
    }

    let script = document.getElementById('gsi-client');
    if (!script) {
      script = document.createElement('script');
      script.src = GSI_SRC;
      script.async = true;
      script.defer = true;
      script.id = 'gsi-client';
      document.body.appendChild(script);
    }
    script.addEventListener('load', renderButton);
    return () => {
      cancelled = true;
      script.removeEventListener('load', renderButton);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Not configured -> render nothing (email/password still works).
  if (!GOOGLE_CLIENT_ID) return null;

  return (
    <div className="auth-google">
      <div className="auth-google-btn" ref={btnRef} aria-label="Continue with Google" />
    </div>
  );
};

export default GoogleSignInButton;
